import { useI18n } from '@upweo/ui';
import { useQuery } from '@tanstack/react-query';

import * as api from '../lib/api.js';
import { usePartner } from '../lib/partner-context.js';

/**
 * Which merchants have installed this partner's apps.
 *
 * Note what is NOT shown: any of the store's actual data. A partner developer's
 * session grants visibility of the installation record only — reading a store's
 * products requires that app's own access token and the scopes the merchant
 * granted.
 */
export const InstallsPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeAccount } = usePartner();

  const installsQuery = useQuery({
    queryKey: ['installs', activeAccount?.id],
    queryFn: () => api.listInstalls(),
    enabled: Boolean(activeAccount),
  });

  const installs = installsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('partner.installs')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Stores using your apps · {installs.length}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-2.5 font-medium">{t('common.store')}</th>
              <th className="px-4 py-2.5 font-medium">App</th>
              <th className="px-4 py-2.5 font-medium">{t('partner.grantedScopes')}</th>
              <th className="px-4 py-2.5 font-medium">{t('common.status')}</th>
              <th className="px-4 py-2.5 font-medium">{t('partner.installed')}</th>
            </tr>
          </thead>
          <tbody>
            {installsQuery.isPending && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!installsQuery.isPending && installs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No installations yet.
                </td>
              </tr>
            )}
            {installs.map((i) => (
              <tr key={i.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3">
                  <div className="font-medium">{i.storeName}</div>
                  <div className="text-xs text-muted-foreground">/{i.storeSlug}</div>
                </td>
                <td className="px-4 py-3">{i.appName}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {i.scopes.map((s) => (
                      <span key={s} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {s}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{i.status.toLowerCase()}</td>
                <td className="px-4 py-3 text-muted-foreground">{api.formatDate(i.installedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
